"use client";
import React, { useRef } from "react";
import { useScroll, useTransform, useSpring } from "motion/react";
import { ProjectType } from "@/components/utils/types";
import { useMediaQuery } from "@/hooks/use-media-query";

import { Hero } from "./Hero";
import { ShowcaseImage } from "./ShowcaseImage";
import { CoreFeatures } from "./CoreFeatures";
import { Adaptability } from "./Adaptability";
import { TechStack } from "./TechStack";
import { Challenges } from "./Challenges";
import { Impact } from "./Impact";
import { CallToAction } from "./CallToAction";

export default function ProjectDetails({ project }: { project: ProjectType }) {
  const container = useRef<HTMLDivElement>(null);

  // Note: Lenis is initialized in the parent Layout/Page via SmoothScrollbar component.
  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  });

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Spring config for smooth, natural animations
  const springConfig = { restDelta: 0.001 };

  // Parallax Transforms with Spring Physics
  const yHeroRaw = useTransform(scrollYProgress, [0, 0.1], ["0%", "50%"]);
  const yHero = useSpring(yHeroRaw, springConfig);

  const opacityHeroRaw = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const opacityHero = useSpring(opacityHeroRaw, springConfig);

  const scaleImgRaw = useTransform(
    scrollYProgress,
    isMobile ? [0.05, 0.2] : [0.1, 0.12],
    isMobile ? [0.8, 1] : [0.7, 1],
  );
  const opacityImgRaw = useTransform(
    scrollYProgress,
    isMobile ? [0.05, 0.2] : [0.1, 0.14],
    [0, 1],
  );
  const opacityImg = useSpring(opacityImgRaw, springConfig);
  const scaleImg = useSpring(scaleImgRaw, springConfig);

  return (
    <div
      ref={container}
      className="relative w-full bg-[#f5f5f7] dark:bg-[#050505] overflow-hidden transition-colors duration-1000 ease-in-out"
    >
      <Hero
        project={project}
        yHero={yHero}
        opacityHero={opacityHero}
      />
      <ShowcaseImage
        project={project}
        scaleImg={scaleImg}
        opacityImg={opacityImg}
      />
      <CoreFeatures project={project} />
      <Adaptability project={project} />
      <TechStack project={project} />
      <Challenges project={project} />
      <Impact project={project} />
      <CallToAction project={project} />
    </div>
  );
}
